"use client";

import isEmpty from "ramda/src/isEmpty";
import { IPassenger } from "$components/PassengerForm/types";
import { ORDER_LOCAL_STORAGE_KEY } from "$constants/strings";
import { createContext } from "helpers/createContext";
import { Offer } from "generated/dist/public/types";
import { stringify } from "helpers/stringify";
import { useEffect, useMemo, useState } from "react";
import { getParsedJson } from "helpers/getParsedJson";
import { useParams } from "next/navigation";

export interface ILocalOrder {
  offer?: Offer;
  qty: number;
  note?: string;
  createdAt: string;
  departureDate: string;
  passengers?: IPassenger[];
  createdTicketsIds?: string[];
}

interface IOrderContext {
  order: Partial<ILocalOrder>;
  setOrder: React.Dispatch<React.SetStateAction<Partial<ILocalOrder>>>;
  isTicketsCreated: boolean;
}

const [OrderContext, useOrderContext] = createContext<IOrderContext>();

const getLocalStorageOrder = (): Partial<ILocalOrder> =>
  getParsedJson<ILocalOrder>(localStorage.getItem(ORDER_LOCAL_STORAGE_KEY)) ||
  {};

export const OrderProvider: ReactFCWithChildrenProp = (props) => {
  const [order, setOrder] = useState<Partial<ILocalOrder>>({});
  const params = useParams();

  const refreshStorageOffers = () => {
    const storageOrder = getLocalStorageOrder();

    if (!order.offer && !!storageOrder?.offer) {
      setOrder(storageOrder);
    }
  };

  useEffect(() => {
    if (params.departureCitySlug && params.arrivalCitySlug) return;

    refreshStorageOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(params)]);

  useEffect(() => {
    if (isEmpty(order)) return;

    localStorage.setItem(ORDER_LOCAL_STORAGE_KEY, stringify(order));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(order)]);

  const isTicketsCreated = useMemo(() => {
    if (!order?.createdTicketsIds?.length) return false;

    return order.createdTicketsIds.length === order?.qty;
  }, [order?.createdTicketsIds?.length, order?.qty]);

  const value = useMemo(
    () => ({ order, setOrder, isTicketsCreated }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stringify(order), isTicketsCreated]
  );

  return <OrderContext.Provider value={value} {...props} />;
};

export { useOrderContext };
